import React, { useState } from "react";
import CustomModal from "../CustomModal";
import TestimonialForm from "./TestiMonialForm";
import { useGetAllTestimonial } from "../../hooks/useGetTestimonial";
import Error from "../utils/Error";
import TestimonialSkeleton from "./TestimonialSkeleton";

const Testimonialnew = () => {
  const [currentItem, setCurrentItem] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [startX, setStartX] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const {
    data: testimonial,
    isLoading,
    error,
    refetch,
  } = useGetAllTestimonial();

  const items = testimonial?.data || [];
  const current = items[currentItem];
  const comment = current?.comments || "";
  const isLong = comment.length > 350;
  const displayComment =
    expanded || !isLong ? comment : `${comment.slice(0, 350)}…`;

  const handlePrevious = () => {
    setCurrentItem((prev) => (prev > 0 ? prev - 1 : prev));
    setExpanded(false);
  };

  const handleNext = () => {
    setCurrentItem((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    setExpanded(false);
  };

  const handleClose = () => setShowModal(false);

  const handleTouchStart = (e) => setStartX(e.touches[0].clientX);

  const handleTouchMove = (e) => {
    if (startX == null) return;
    const diffX = startX - e.touches[0].clientX;
    if (diffX > 50) {
      handleNext();
      setStartX(null);
    } else if (diffX < -50) {
      handlePrevious();
      setStartX(null);
    }
  };

  return (
    <section className="w-full bg-surface-white py-16 md:py-24 text-left">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <span className="inline-block bg-cloud-mist text-spectrum-blue text-xs font-bold tracking-wider px-3.5 py-1 rounded-brand mb-3 font-headline">
              OUR TESTIMONIAL
            </span>
            <h2 className="font-headline text-3xl md:text-4xl text-ink leading-tight">
              <span className="font-light">What Families Say</span>
              <span className="font-extrabold text-spectrum-blue block sm:inline">
                {" "}
                About Social Spectrum
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 bg-cloud-mist text-spectrum-blue font-headline font-semibold text-[14px] px-5 py-2.5 rounded-brand hover-mist-deep self-start sm:self-auto transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
          >
            Add Testimonial
          </button>
        </div>

        {isLoading ? (
          <TestimonialSkeleton />
        ) : error ? (
          <Error
            title="Testimonials aren’t loading"
            message={error.message}
            onRetry={refetch}
          />
        ) : items.length ? (
          <div
            className="max-w-4xl min-h-[280px] sm:min-h-[300px] bg-mist-panel border border-cloud-mist rounded-brand p-8 sm:p-10 shadow-brand-sm relative flex flex-col"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            <div className="absolute top-6 right-6 pointer-events-none" aria-hidden="true">
              <svg
                fill="none"
                height="28"
                stroke="currentColor"
                className="text-sunshine"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="28"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>

            <p className="font-headline font-medium text-lg sm:text-xl text-ink leading-relaxed mb-6 max-w-[760px] italic pr-8 flex-1 min-h-[7.5rem] sm:min-h-[8.5rem]">
              “{displayComment}”
              {isLong && !expanded ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="not-italic font-semibold text-spectrum-blue hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
                  >
                    Read More
                  </button>
                </>
              ) : null}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-cloud-mist gap-4 mt-auto">
              <div className="min-w-0">
                <h3 className="font-headline font-bold text-ink text-base capitalize">
                  {current.name}
                </h3>
                <p className="font-body text-xs text-muted">
                  {current.organization || "Parent & Community Member"}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentItem === 0}
                  aria-label="Previous testimonial"
                  className="w-10 h-10 rounded-brand bg-surface-white border border-cloud-mist hover:bg-cloud-mist text-spectrum-blue flex items-center justify-center transition-colors disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={currentItem >= items.length - 1}
                  aria-label="Next testimonial"
                  className="w-10 h-10 rounded-brand bg-spectrum-blue text-surface-white hover:opacity-90 flex items-center justify-center transition-opacity shadow-brand-sm disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M9 18l6-6-6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <h4 className="text-24 text-left font-extrabold text-spectrum-blue mt-4 font-headline">
            No Testimonials Available
          </h4>
        )}
      </div>

      <CustomModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Create Testimonial"
      >
        <div className="w-full">
          <TestimonialForm showModal={handleClose} />
        </div>
      </CustomModal>
    </section>
  );
};

export default Testimonialnew;
